// The DRAFT → PAYLOAD build: the matrix→weeks pivot plus the zod validation
// that turns a composer Draft into a NewProgram payload.
//
// Split out of ./programEditBuild (CN-program-edit-build-split) when that file
// reached 497/500 with PE-34's isLoadBearingKind. The seam is a real one, not
// a line count: what stayed behind is the CELL VOCABULARY — how one cell's
// text becomes a number, which kinds carry which value — and it is read by the
// editor's inputs on every keystroke. What moved here runs once, at Save, over
// a whole draft. Two different questions, two different call rhythms.
//
// Keep free of React and any UI import.

import { NewProgram, type NewProgram as NewProgramT } from "./schemas/program.js";
import type { SetIntensity as SetIntensityT } from "./schemas/program.js";
import type { Draft } from "./programEdit.js";
import { alignGroupSlots } from "./programEditRoundSets.js";
import { PROGRAM_SCHEMA_VERSION } from "./programNewFields.js";
import {
  setAuthoredLocale,
  type AuthoredText as AuthoredTextT,
} from "./authoredText.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { buildWorkForCell } from "./programEditTimeAxis.js";
import {
  amrapOf,
  buildIntensity,
  isCellAbsent,
  kindAt,
  parseRepsToken,
  type BuildReasonCode,
} from "./programEditBuild.js";
import { locateDraftProblems } from "./programEditProblems.js";
import { cellRepsRange } from "./programRepsRange.js";
import { roundTypesForSave } from "./programRoundTypes.js";

export type BuildResult =
  | { ok: true; payload: NewProgramT }
  | { ok: false; reason: string; code: BuildReasonCode };

// `cellUnit` is the unit the draft's absolute cells are expressed in — see
// buildIntensity. It must be the SAME unit programToDraft seeded them with.
// ⛔ Required (CN-26 wave 3): see programToDraft.
export const buildPayload = (
  draft: Draft,
  cellUnit: UnitsPreference,
  // CO-17/CO-20 — the view active when the "+" was pressed. Stamped into
  // the payload ONLY at creation: `Draft` carries no such field (a
  // program does not change hats on edit), so the caller
  // (useProgramEditor's saveProgram) passes this argument on the
  // create path and omits it on the edit path — the server preserves the
  // stored value from the existing row regardless of what a stale client
  // still sends (programsLifecycle.ts), so an edit-time value here would
  // be inert, not merely redundant. Absent ⇒ the schema's own default.
  authoredAs?: NewProgramT["authoredAs"],
): BuildResult => {
  // #336: the checks live in locateDraftProblems, which also tells the phone
  // editor WHERE each problem is. The save refuses on the first one, except
  // the blank name: that is the editor's guard (useProgramEditor), and here
  // the schema decides, as it always has. A name blank in the language being
  // written but present in another still builds (draftContent.sameContent
  // reads this too).
  const problem = locateDraftProblems(draft, cellUnit).find(
    (p) => p.code !== "programNameEmpty",
  );
  if (problem) return { ok: false, reason: problem.reason, code: problem.code };

  // Sanitise singleton groups per session before serialising. A session
  // where the coach marked one slot as "Superset A" without adding a
  // partner shouldn't leak the id into storage. Mirrors WorkoutLog's
  // and PlansCard's pre-save passes.
  const cleaned = draft.sessions.map((session) => {
    const counts = new Map<string, number>();
    for (const slot of session.slots) {
      if (slot.group) {
        counts.set(slot.group.id, (counts.get(slot.group.id) ?? 0) + 1);
      }
    }
    const deSingletoned = session.slots.map((slot) =>
      slot.group && (counts.get(slot.group.id) ?? 0) < 2
        ? { ...slot, group: null }
        : slot,
    );
    // Force symmetric supersets on save (Antenore 2026-06-01, inbox 11f01866
    // pt 2): a superset renders as interleaved rounds, so members with
    // different set counts leave orphaned rounds in the logger. Each member is
    // padded up to its group's longest, round by round (#336 S1, Q7), never
    // trimmed: alignGroupSlots, the editor's own join rule. Mirrors the
    // resolver's read-time guard.
    const groupIds = new Set(deSingletoned.flatMap((slot) => (slot.group ? [slot.group.id] : [])));
    return {
      ...session,
      slots: [...groupIds].reduce((acc, gid) => alignGroupSlots(acc, gid, "pad"), deSingletoned),
    };
  });

  // PE-21 — LOSSLESS ROUND-TRIP FOR TRANSLATIONS.
  //
  // A program PATCH replaces the whole object, so emitting the plain
  // string the editor happens to be showing would DELETE every other
  // language on save. Each edited string is grafted back into the
  // language the draft is writing, leaving the rest of the stored value
  // untouched. Same rule as `variantsRaw` for multi-variant cadence
  // (§3.2) — the editor must never destroy what it can't display.
  //
  // ⛔ Naked strings stay naked: a program that was never translated
  // does not silently become an object the first time it is saved.
  const graft = (
    raw: AuthoredTextT | undefined,
    text: string,
  ): AuthoredTextT | undefined => {
    const trimmed = text.trim();
    // ⛔ The shape check comes FIRST. `editLocale` is now null for every
    // program written before PE-21, so testing it first would let a
    // per-language object be collapsed to the one string on screen —
    // silently, with no error, destroying every other language in that
    // field. Whether we know the writing language decides what we WRITE,
    // never whether we preserve what is already there.
    // `== null`: a draft restored from device JSON has `null` where an
    // absent raw was (`[undefined]` serialises to `[null]`), and must save.
    if (raw == null || typeof raw === "string")
      return trimmed || undefined;
    if (!draft.editLocale) return raw;
    return setAuthoredLocale(raw, draft.editLocale, trimmed);
  };

  const roundTypes = roundTypesForSave(draft);
  const payload: NewProgramT = {
    name: graft(draft.nameRaw, draft.name) ?? draft.name.trim(),
    description: graft(draft.descriptionRaw, draft.description),
    // CO-17 — see the parameter note above: only ever supplied by the
    // caller on create, so an edit save falls through to the schema's
    // own default rather than asserting a hat this function has no way
    // to know the program actually wears.
    authoredAs: authoredAs ?? "athlete",
    // PE-21 — record the language the text was WRITTEN in, so the read
    // chain can go reader → author → any available instead of falling
    // through to English. Absent when the draft has no writing language:
    // a program from before PE-21 whose language nobody has declared
    // stays undeclared rather than being guessed.
    // ⛔ `declaredAuthorLocale`, NOT `editLocale`: the tab in view is not
    // a claim about the program. Browsing the French and saving must not
    // make an Italian program French for every reader in the app.
    ...(draft.declaredAuthorLocale
      ? { authorLocale: draft.declaredAuthorLocale }
      : {}),
    cycleWeeks: draft.cycleWeeks,
    sessionsPerWeek: draft.sessionsPerWeek,
    // #336 S2: sent only when a round is not normal.
    ...(roundTypes ? { roundTypes } : {}),
    ...(draft.focus ? { focus: draft.focus } : {}),
    ...(draft.origin ? { origin: draft.origin } : {}),
    status: draft.status,
    // #336: this editor knows the fields an old client drops (Q1).
    schemaVersion: PROGRAM_SCHEMA_VERSION,
    // ME-3: targets are no longer carried on the program — they live on the
    // athlete's AthleteTargets doc now, so the save path neither sends nor
    // preserves them.
    // Preserve existing assignees; the chip-input emails ride alongside
    // via assignedToEmails (server resolves + dedupes).
    assignedTo: draft.assignedTo,
    ...(draft.assigneeEmails.length > 0
      ? { assignedToEmails: draft.assigneeEmails }
      : {}),
    sessions: cleaned.map((s) => ({
      id: s.id || undefined,
      name: graft(s.nameRaw, s.name) ?? s.name.trim(),
      notes: graft(s.notesRaw, s.notes),
      slots: s.slots.map((slot, slotIdx) => {
        // Pivot rows[]→ weeks[]: for each week, take the w-th cell of
        // every row and assemble that week's sets[]. Inactive weeks
        // emit sets: [] (slot doesn't run that week — schema §WeekPlan).
        const weeks = Array.from({ length: draft.cycleWeeks }, (_, w) => ({
          sets: slot.inactiveWeeks[w]
            ? []
            : slot.setRows.filter((row) => !isCellAbsent(row, w)).map((row) => {
                const cell = row.cells[w]!;
                const tok = parseRepsToken(cell.reps) ?? {
                  reps: 0,
                  amrap: false,
                };
                const amrap = amrapOf(cell);
                const kind = kindAt(row, w);
                const intensity =
                  kind === "none"
                    ? { kind: "none" as const }
                    : (buildIntensity(
                        kind,
                        cell.intensityValue,
                        cellUnit,
                      ) as SetIntensityT);
                const notes = graft(
                  row.notesRawPerCell?.[w],
                  row.notesPerCell[w] ?? "",
                );
                const { amrapAllowed, ...time } = buildWorkForCell(
                  row.workKind,
                  cell.workDurationSec,
                  cell.restSec,
                );
                // #336 S3: null on an AMRAP or timed set (Q6).
                const range = cellRepsRange(row, cell);
                return {
                  reps: tok.reps,
                  ...(range ? { repsMin: range.min, repsMax: range.max } : {}),
                  ...(amrap && amrapAllowed ? { amrap: true } : {}),
                  ...(amrap && amrapAllowed && cell.amrapStop ? { amrapStop: { ...cell.amrapStop } } : {}),
                  intensity,
                  ...time,
                  notes,
                };
              }),
        }));
        // Lossless round-trip (§3.2): a stored slot's variants survive
        // verbatim — the editor's exercise pick grafts onto variants[0]
        // (keeping its cadence/phaseOffset), extra variants re-emit
        // untouched. Fresh slots synthesise the single every-week variant.
        const v0 = slot.variantsRaw[0];
        const variants = v0
          ? [
              {
                ...v0,
                exerciseId: slot.exerciseId,
                exerciseName: slot.exerciseName,
              },
              ...slot.variantsRaw.slice(1),
            ]
          : [
              {
                exerciseId: slot.exerciseId,
                exerciseName: slot.exerciseName,
                cadence: "every-week" as const,
                phaseOffset: 0,
              },
            ];
        return {
          id: slot.id || undefined,
          position: slotIdx,
          variants,
          weeks,
          instructions: graft(slot.instructionsRaw, slot.instructions),
          amrapLastSet: slot.amrapLastSet || undefined,
          maxAttempt: slot.maxAttempt || undefined,
          rpeEnabled: slot.rpeEnabled || undefined,
          rirEnabled: slot.rirEnabled || undefined,
          ...(slot.rowId ? { rowId: slot.rowId } : {}),
          ...(slot.side ? { side: slot.side } : {}),
          // Group only persists when ≥2 slots in the same session share
          // the id; otherwise resolveProgramSession would drop it on
          // resolve. The singleton filter above already nulled lone groups.
          ...(slot.group
            ? {
                group: {
                  id: slot.group.id,
                  kind: slot.group.kind,
                  // Carried verbatim (HI-1): the editor cannot author a
                  // cadence yet, so re-emitting is the only way a stored
                  // one survives a save from this screen.
                  ...(slot.group.cadence !== undefined
                    ? { cadence: slot.group.cadence }
                    : {}),
                  // PE-30 — the group's label survives the save. Without
                  // this the picker asked for a name the program could not
                  // keep, and the coach's superset came back numbered.
                  ...(slot.group.name !== undefined
                    ? { name: slot.group.name }
                    : {}),
                },
              }
            : {}),
        };
      }),
    })),
  };
  const validated = NewProgram.safeParse(payload);
  if (!validated.success) {
    const first = validated.error.issues[0];
    const path = first?.path.join(".") ?? "?";
    return {
      ok: false,
      reason: `${path}: ${first?.message ?? ""}`,
      code: "schemaInvalid",
    };
  }
  return { ok: true, payload: validated.data };
};
